import {Injectable} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {GtConfigurationService} from '@app/services/gt-configuration.service';
import {GtNameValueService} from '@app/services/gt-name-value.service';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {AbsenceReason} from '@gt-api';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {StatusModel} from '@app/models/status.model';
import {StatusAbsentModel} from '@app/models/status-absent.model';
import {StatusAvailableModel} from '@app/models/status-available.model';
import {absenceActionNameValues, forwardingTargetNameValues} from '@app/models/status.constants';

@Injectable({
  providedIn: 'root'
})
export class GtStatusModelService {
  // login+oname -> statusmodels
  private statusModelStore = new BehaviorSubject<Map<string, StatusModel[]>>(new Map<string, StatusModel[]>());

  statusModels$ = (oname: string, login: string) => this.statusModelStore.pipe(map(store => {
    return store.get(login + oname) ?? [];
  }));
  constructor(
    private sanitizer: DomSanitizer,
    private configurationService: GtConfigurationService,
    private absenceService: GtAbsenceService,
    private nameValueService: GtNameValueService
  ) {}

  sanitizeImg(status: StatusModel) {
    const url = 'data:image/png;base64,' + status.imgData;
    status.img = this.sanitizer.bypassSecurityTrustUrl(url);
  }

  getStatusModels(oname: string, login: string): Observable<StatusModel[]> {
    if (!this.statusModelStore.value.has(login + oname)) {
      this.fetchStatusModels(oname, login);
    }
    return of(this.statusModelStore.value.get(login + oname));
  }

  fetchStatusModels(oname: string, login: string): void {
    this.absenceService.absenceReasons$(login).subscribe(reasons => {
      const statusModels = reasons.map(r => this.convertAbsenceReason(r, login, oname));
      const statusModelMap = this.statusModelStore.getValue();
      statusModelMap.set(login + oname, statusModels);
      this.statusModelStore.next(statusModelMap);
    });
  }

  convertAbsenceReason(absenceReason: AbsenceReason, login: string, organization: string): StatusModel {
    if (absenceReason.id !== 0) {
      const statusAbsentModel = BuilderFactory.createBuilder(StatusAbsentModel)
        .reasonId(absenceReason.id)
        .organization(organization)
        .message(absenceReason.value)
        .imgData(absenceReason.imageData)
        .actionId(absenceReason.defaultAction)
        .defaultActionId(absenceReason.defaultAction)
        .defaultDuration(absenceReason.defaultDuration)
        .readReason(absenceReason.defaultReadReason)
        .defaultReadReason(absenceReason.defaultReadReason)
        .readTime(absenceReason.defaultReadTime)
        .defaultReadTime(absenceReason.defaultReadTime)
        .tempLogoutAgent(Boolean(absenceReason.defaultTempLogoutAgent))
        .defaultTempLogoutAgent(Boolean(absenceReason.defaultTempLogoutAgent))
        .login(login)
        .build();

      this.setForwardingNumber(statusAbsentModel);
      this.sanitizeImg(statusAbsentModel);
      return statusAbsentModel;
    } else {
      const statusAvailable = BuilderFactory.createBuilder(StatusAvailableModel)
        .reasonId(absenceReason.id)
        .organization(organization)
        .message(absenceReason.value)
        .imgData(absenceReason.imageData)
        .forwardingDelay(0)
        .forwardingTarget(0)
        .forwardingType(0)
        .login(login)
        .build();

      this.getActiveProfile(statusAvailable);
      this.sanitizeImg(statusAvailable);

      return statusAvailable;
    }
  }

  setForwardingNumber(status: StatusAbsentModel) {
    const numberType = absenceActionNameValues.find(t => t.forwardingTarget === status.actionId);
    if (numberType) {
      this.nameValueService.getEndUserNameValue(numberType.name, status.organization, status.login).subscribe(r => {
        status.forwardingNumber = r;
      });
    } else {
      status.forwardingNumber = '';
    }
  }

  getActiveProfile(status: StatusAvailableModel) {
    this.configurationService.getActiveProfile(status.organization, status.login).subscribe(profile => {
      if (profile <= 4) {
        // forwarding after
        status.forwardingType = 0;
        status.forwardingTarget = profile;
        this.nameValueService
          .getEndUserNameValue('NoReplyTimeFN', status.organization, status.login)
          .subscribe(nr => (status.forwardingDelay = Number(nr)));
      } else if (profile <= 8) {
        // forwarding immediately
        status.forwardingType = 4;
        status.forwardingTarget = profile - 4;
      } else {
        // no forwarding
        status.forwardingType = 9;
      }

      const numberType = forwardingTargetNameValues.find(t => t.forwardingTarget === status.forwardingTarget);

      if (numberType) {
        this.nameValueService.getEndUserNameValue(numberType.name, status.organization, status.login).subscribe(r => {
          status.forwardingNumber = r;
        });
      }
    });
  }
}
